import { useState, useEffect } from 'react';
import { 
    Button, 
    Group, 
    Stack, 
    Text,
    LoadingOverlay,
    Tabs,
    Checkbox,
    Flex,
    Select,
    Alert,
    Center
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { SortableTable, ColumnDef } from '../SortableTable';
import styles from './UserRoleFunctionManagement.module.css';
import { 
    getRoleDetails, 
    saveUserRole,
    getRoles,
    getAddressSystems
} from '../../services/UserRoleService';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";
import { 
    RoleDetailsResponse, 
    FunctionInfo,
    UserRoleRequest,
    RoleListItem,
    AddressSystem
} from '../../types/UserRole';

interface UserRoleFunctionManagementProps {
    roleId?: number;
    addressSystemId?: number;
    onBack?: () => void;
}

export default function UserRoleFunctionManagement({
  roleId: initialRoleId, addressSystemId: initialAddressSystemId, onBack }: UserRoleFunctionManagementProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 329, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const [roleDetails, setRoleDetails] = useState<RoleDetailsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedFunctions, setSelectedFunctions] = useState<Set<number>>(new Set());
    const [activeTab, setActiveTab] = useState<string>('');
    
    // Add states for dropdowns
    const [roles, setRoles] = useState<RoleListItem[]>([]);
    const [addressSystems, setAddressSystems] = useState<AddressSystem[]>([]);
    const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string>(initialAddressSystemId?.toString() || '');
    const [selectedRoleId, setSelectedRoleId] = useState<string>(initialRoleId?.toString() || '');

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (selectedRoleId && selectedAddressSystemId) {
            loadRoleDetails();
        }
    }, [selectedRoleId, selectedAddressSystemId]);

    useEffect(() => {
        if (roleDetails && roleDetails.functionGroups.length > 0) {
            // Set the first tab as active by default
            setActiveTab(roleDetails.functionGroups[0].id.toString());
            
            // Initialize selected functions
            const initialSelected = new Set<number>();
            roleDetails.functionGroups.forEach(group => {
                group.functions.forEach(func => {
                    if (func.hasAccess) {
                        initialSelected.add(func.id);
                    }
                });
            });
            setSelectedFunctions(initialSelected);
        }
    }, [roleDetails]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [rolesData, addressSystemsData] = await Promise.all([
                getRoles(),
                getAddressSystems()
            ]);
            setRoles(rolesData);
            setAddressSystems(addressSystemsData);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_USER_ROLE_FUNCTION_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_USER_ROLE_FUNCTION_LOAD_INITIAL_FAILED") || "Failed to load initial data",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadRoleDetails = async () => {
        if (!selectedRoleId || !selectedAddressSystemId) return;
        
        setLoading(true);
        try {
            const details = await getRoleDetails(parseInt(selectedRoleId), parseInt(selectedAddressSystemId));
            setRoleDetails(details);
        } catch (error) {
            console.error('Error loading role details:', error);
            notifications.show({
                title: getTranslation("LABEL_USER_ROLE_FUNCTION_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_USER_ROLE_FUNCTION_LOAD_ROLE_DETAILS_FAILED") || `Failed to load role details: ${error instanceof Error ? error.message : 'Unknown error'}`,
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFunctionToggle = (functionId: number) => {
        const newSelected = new Set(selectedFunctions);
        if (newSelected.has(functionId)) {
            newSelected.delete(functionId);
        } else {
            newSelected.add(functionId);
        }
        setSelectedFunctions(newSelected);
    };

    const handleSave = async () => {
        if (!roleDetails) return;

        setSaving(true);
        try {
            const request: UserRoleRequest = {
                roleId: roleDetails.roleId,
                addressSystemId: roleDetails.addressSystemId,
                appDefinition: roleDetails.appDefinition,
                functionIds: Array.from(selectedFunctions)
            };

            const success = await saveUserRole(request);
            if (success) {
                notifications.show({
                    title: getTranslation("LABEL_USER_ROLE_FUNCTION_SUCCESS_TITLE") || "Success",
                    message: getTranslation("LABEL_USER_ROLE_FUNCTION_UPDATED") || "Role functions updated successfully",
                    color: 'green',
                    withBorder: true,
                    withCloseButton: true,
                    autoClose: 5000,
                    className: 'notification-success'
                });
                // Reload details to reflect changes
                await loadRoleDetails();
            } else {
                notifications.show({
                    title: getTranslation("LABEL_USER_ROLE_FUNCTION_ERROR_TITLE") || "Error",
                    message: getTranslation("LABEL_USER_ROLE_FUNCTION_UPDATE_FAILED") || "Failed to update role functions",
                    color: 'red',
                    withBorder: true,
                    withCloseButton: true,
                    autoClose: 5000,
                    className: 'notification-error'
                });
            }
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_USER_ROLE_FUNCTION_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_USER_ROLE_FUNCTION_UPDATE_FAILED") || "Failed to update role functions",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setSaving(false);
        }
    };

    const renderFunctionTable = (functions: FunctionInfo[]) => {
        const columns: ColumnDef<FunctionInfo>[] = [
            { 
                header: getTranslation("LABEL_USER_ROLE_FUNCTION_NAME") || "Name", 
                accessor: 'name', 
                sortable: true, 
                width: '45%' 
            },
            { 
                header: getTranslation("LABEL_USER_ROLE_FUNCTION_CODE") || "Code", 
                accessor: 'functionCode', 
                sortable: true, 
                width: '45%' 
            }
        ];

        return (
            <div className={styles.functionTableContainer}>
                <SortableTable
                    data={functions}
                    columns={columns}
                    pageSize={functions.length} // Show all functions, let container handle scrolling
                    rowKey={(row) => row.id}
                    showPagination={false} // Disable pagination, use container scrolling
                    actionColumns={[
                        {
                            label: getTranslation("LABEL_USER_ROLE_FUNCTION_PERMISSIONS") || "Permissions",
                            width: '10%',
                            render: (row) => (
                                <Flex justify="center">
                                    <Checkbox
                                        checked={selectedFunctions.has(row.id)}
                                        onChange={() => handleFunctionToggle(row.id)}
                                    />
                                </Flex>
                            )
                        }
                    ]}
                />
                {functions.length === 0 && (
                    <Center mt="xl">
                        <Text>{getTranslation("LABEL_USER_ROLE_FUNCTION_NONE_AVAILABLE") || "No functions available."}</Text>
                    </Center>
                )}
            </div>
        );
    };

    return (
        <>
            <LoadingOverlay visible={loading || saving} />

            <Group align="end" mb="md" className={styles.selectGroup}>
                <Select
                    label={getTranslation("LABEL_ADDRESS_SYSTEM") || "Address System"}
                    placeholder={getTranslation("PLACEHOLDER_SELECT_ADDRESS_SYSTEM") || "Select address system"}
                    value={selectedAddressSystemId}
                    onChange={(value) => setSelectedAddressSystemId(value || '')}
                    data={addressSystems.map(system => ({
                        value: system.id.toString().trim(),
                        label: system.name.trim()
                    }))}
                    searchable
                    clearable
                    allowDeselect={false}
                    className={styles.selectInput}
                    spellCheck={false}
                />

                <Select
                    label={getTranslation("LABEL_ROLE") || "Role"}
                    placeholder={getTranslation("PLACEHOLDER_SELECT_A_ROLE") || "Select a role"}
                    value={selectedRoleId}
                    onChange={(value) => setSelectedRoleId(value || '')}
                    data={roles.map(role => ({
                        value: role.id.toString(),
                        label: role.name
                    }))}
                    searchable
                    clearable
                    allowDeselect={false}
                    disabled={!selectedAddressSystemId}
                    className={styles.selectInput}
                    spellCheck={false}
                />
            </Group>

                {/* Alert messages for selection state */}
                {!selectedAddressSystemId && (
                    <Alert icon={<IconAlertCircle size={18} />} color="blue">
                        {getTranslation("LABEL_USER_ROLE_FUNCTION_SELECT_ADDRESS_SYSTEM") || "Please select an address system to continue."}
                    </Alert>
                )}

                {selectedAddressSystemId && !selectedRoleId && (
                    <Alert icon={<IconAlertCircle size={18} />} color="blue">
                        {getTranslation("LABEL_USER_ROLE_FUNCTION_SELECT_ROLE") || "Please select a role to continue."}
                    </Alert>
                )}

                {/* Show role details only when both are selected */}
                {selectedAddressSystemId && selectedRoleId && roleDetails && (
                    <>
                        <Stack gap="md">
                            <Text fw={500} size="lg" mt="xl" mb="xs">
                                {getTranslation("LABEL_USER_ROLE_FUNCTION_USER_RIGHTS") || "User Rights"}
                            </Text>
                            {roleDetails.functionGroups.length > 0 ? (
                                <Tabs
                                    value={activeTab}
                                    onChange={(value) => setActiveTab(value || '')}
                                    variant="pills"
                                >
                                <Tabs.List>
                                    {roleDetails.functionGroups.map((group) => (
                                        <Tabs.Tab
                                            key={group.id}
                                            value={group.id.toString()}
                                        >
                                            {group.name}
                                        </Tabs.Tab>
                                    ))}
                                </Tabs.List>

                                {roleDetails.functionGroups.map((group) => (
                                    <Tabs.Panel
                                        key={group.id}
                                        value={group.id.toString()}
                                        pt="md"
                                    >
                                        {group.functions.length > 0 ? (
                                            renderFunctionTable(group.functions)
                                        ) : (
                                            <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
                                                {getTranslation("LABEL_USER_ROLE_FUNCTION_NONE_IN_GROUP") || "No functions available in this group."}
                                            </Alert>
                                        )}
                                    </Tabs.Panel>
                                ))}
                            </Tabs>
                        ) : (
                            <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
                                {getTranslation("LABEL_USER_ROLE_FUNCTION_NO_GROUPS") || "No function groups available for this role."}
                            </Alert>
                        )}
                        <Group justify="flex-left" mt="md">
                            <Button
                                onClick={handleSave}
                                loading={saving}
                                size="md"
                                color="#009DF0"
                            >
                                {getTranslation("LABEL_USER_ROLE_FUNCTION_SAVE") || "Save"}
                            </Button>
                        </Group>
                        </Stack>
                    </>
                )}
        </>
    );
}
